import { InputOTP6 } from "@/components/molecules/otp";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AuthAPI } from "@/features/auth/api/auth";
import { useUser } from "@/features/auth/hooks/use-user";
import { getCaptchaToken } from "@/features/utils/captcha";
import { useTranslation } from "@/features/utils/i18n";
import { useState } from "react";
import { toast } from "sonner";

type Step = "idle" | "verify-otp" | "set-password";

export const PasswordTable = () => {
  const { t } = useTranslation();
  const { data: user } = useUser();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [step, setStep] = useState<Step>("idle");
  const [isLoading, setIsLoading] = useState(false);
  const [emailPreSignedToken, setEmailPreSignedToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [retryTimeLeft, setRetryTimeLeft] = useState(0);

  // Start retry timer
  const startRetryTimer = () => {
    setRetryTimeLeft(60);
    const interval = setInterval(() => {
      setRetryTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleChangePassword = async () => {
    if (!user?.email) return;

    setIsLoading(true);
    try {
      await AuthAPI.emailChallenge({
        email: user.email,
        captchaToken: await getCaptchaToken("email_challenge"),
      });

      toast.success(t("email.codeSent", { email: user.email }));
      setStep("verify-otp");
      setIsDialogOpen(true);
      startRetryTimer();
    } catch (error) {
      console.error(error);
      const errorMessage =
        error instanceof Error ? error.message : t("email.sendFailed");
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpVerify = async (otp: string) => {
    if (otp.length !== 6 || !user?.email) return;

    setIsLoading(true);
    try {
      const result = await AuthAPI.emailVerify({
        email: user.email,
        otp,
      });

      if (result.success && result.preSignedToken) {
        setEmailPreSignedToken(result.preSignedToken);
        toast.success(t("email.verified"));
        setStep("set-password");
      }
    } catch (error) {
      console.error(error);
      const errorMessage =
        error instanceof Error ? error.message : t("otp.invalid");
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error(t("signup.passwordRequirements"));
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(t("signup.passwordMismatch"));
      return;
    }

    setIsLoading(true);
    try {
      await AuthAPI.passwordSet({
        emailPreSignedToken,
        password: newPassword,
      });

      toast.success(t("security.password.changeSuccess"));

      // Reset state and close dialog
      setIsDialogOpen(false);
      setStep("idle");
      setEmailPreSignedToken("");
      setNewPassword("");
      setConfirmPassword("");

      // Reload user data to reflect password_hash change
      window.location.reload();
    } catch (error) {
      console.error(error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : t("security.password.changeFailed");
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!user?.email) return;

    setIsLoading(true);
    try {
      await AuthAPI.emailChallenge({
        email: user.email,
        captchaToken: await getCaptchaToken("email_challenge"),
      });

      toast.success(t("email.codeSent", { email: user.email }));
      startRetryTimer();
    } catch (error) {
      console.error(error);
      const errorMessage =
        error instanceof Error ? error.message : t("email.sendFailed");
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setStep("idle");
    setEmailPreSignedToken("");
    setNewPassword("");
    setConfirmPassword("");
  };

  return (
    <>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t("security.password.description")}
        </p>

        <div className="rounded-sm border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("security.password.title")}</TableHead>
                <TableHead className="w-[150px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium">
                  {t("security.password.passwordStatus")}
                </TableCell>
                <TableCell>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleChangePassword}
                    disabled={isLoading}
                  >
                    {t("security.password.change")}
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Verification Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent>
          {step === "verify-otp" && (
            <>
              <DialogHeader>
                <DialogTitle>{t("security.password.verifyEmail")}</DialogTitle>
                <DialogDescription>
                  {t("email.codeSent", { email: user?.email })}
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="flex flex-col items-center justify-center gap-2">
                  <Label>{t("otp.label")}</Label>
                  <InputOTP6
                    required
                    onChange={(value) => {
                      if (value.length === 6) {
                        handleOtpVerify(value);
                      }
                    }}
                    disabled={isLoading}
                  />
                </div>
              </div>
              <DialogFooter className="flex-col sm:flex-col gap-2">
                <Button
                  type="button"
                  variant="link"
                  onClick={handleResendOtp}
                  disabled={retryTimeLeft > 0 || isLoading}
                  className="w-full"
                >
                  {retryTimeLeft > 0
                    ? t("actions.retryIn", { seconds: retryTimeLeft })
                    : t("actions.resendCode")}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCloseDialog}
                  className="w-full"
                >
                  {t("common.cancel")}
                </Button>
              </DialogFooter>
            </>
          )}

          {step === "set-password" && (
            <>
              <DialogHeader>
                <DialogTitle>{t("security.password.changeTitle")}</DialogTitle>
                <DialogDescription>
                  {t("security.password.changeDescription")}
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="new-password">
                    {t("password.newPassword")}
                  </Label>
                  <Input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    placeholder={t("password.placeholder")}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={isLoading}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("signup.passwordRequirements")}
                  </p>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="confirm-password">
                    {t("signup.confirmPassword")}
                  </Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    placeholder={t("signup.confirmPasswordPlaceholder")}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={handleCloseDialog}
                  disabled={isLoading}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  onClick={handleSetPassword}
                  disabled={
                    !newPassword ||
                    !confirmPassword ||
                    newPassword.length < 8 ||
                    isLoading
                  }
                >
                  {isLoading
                    ? t("actions.loading")
                    : t("security.password.change")}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
